import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import CloseIcon from '@mui/icons-material/Close';
import { IconButton } from '@mui/material';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { useTheme } from '@mui/material/styles';
import React, { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { TIMELINE_HEIGHT } from '../constants';
import { getContentStyle } from './getContentStyle';
import { getTimelineStyles } from './getTimelineStyles';

// Define a keyframes animation for the pulse effect
const pulseKeyframes = `
@keyframes pulse {
  0% {
    transform: scale(1);
    opacity: 0.7;
  }
  50% {
    transform: scale(1.2);
    opacity: 1;
  }
  100% {
    transform: scale(1);
    opacity: 0.7;
  }
}
`;

// Use forwardRef to expose methods to parent components
const Timeline = forwardRef(({ height = TIMELINE_HEIGHT, bottomOffset = 0, toolbarPosition, onItemsChange, filterStore, currentView }, ref) => {
    const theme = useTheme();
    const [timelineItems, setTimelineItems] = useState([]);
    const [isDragOver, setIsDragOver] = useState(false);
    const [insertIndex, setInsertIndex] = useState(-1);
    const containerRef = useRef(null);
    const styles = getTimelineStyles(theme, height);

    // Inject the keyframes animation style when component mounts
    useEffect(() => {
        // Create and inject style element for keyframes
        const styleEl = document.createElement('style');
        styleEl.textContent = pulseKeyframes;
        document.head.appendChild(styleEl);

        // Clean up on unmount
        return () => {
            document.head.removeChild(styleEl);
        };
    }, []);

    // Notify parent component when items change
    useEffect(() => {
        if (typeof onItemsChange === 'function') {
            onItemsChange(timelineItems);
        }
    }, [timelineItems, onItemsChange]);

    // Expose methods to parent components through ref
    useImperativeHandle(ref, () => ({
        // Method to add a clip to the timeline
        addClip: (data, position = -1) => {
            addClipToTimeline(data, position);
        },
        // Method to get the current number of items
        getItemCount: () => timelineItems.length
    }));

    // Add clip to timeline with optional position parameter
    const addClipToTimeline = (data, position = -1) => {
        if (!data) return;

        const newClip = {
            id: `timeline-${Date.now()}`, // Generate a unique ID
            video_id: data.id,
            row: data.row,
            index: data.index,
            label: `Clip ${timelineItems.length + 1}`,
            thumbnail: data.thumbnail,
            sentence: data.sentence
        };

        setTimelineItems(prevItems => {
            // If position is -1 or invalid, append to the end
            let updatedItems;
            if (position < 0 || position >= prevItems.length) {
                updatedItems = [...prevItems, newClip];
            } else {
                // Otherwise, insert at the specified position
                updatedItems = [...prevItems];
                updatedItems.splice(position, 0, newClip);
            }

            // Update clip labels to reflect new order
            const finalItems = updatedItems.map((item, idx) => ({
                ...item,
                label: `Clip ${idx + 1}`
            }));

            // Update filterStore with the new selection order
            if (filterStore && currentView) {
                // Get the video_ids in the correct order, preserving duplicates
                const newOrderedSelections = finalItems.map(item => item.video_id).filter(id => id !== undefined);

                // Directly set the selected sentences array in the filterStore
                // This preserves duplicates that would be lost if we used setSelectedSentences for each item
                if (newOrderedSelections.length > 0) {
                    filterStore.resetSelectedSentences(currentView);
                    filterStore.selectedSentences[currentView] = newOrderedSelections;
                }
            }

            return finalItems;
        });

        // Scroll to show the new item if at the end
        if (position === -1 && containerRef.current) {
            setTimeout(() => {
                containerRef.current.scrollLeft = containerRef.current.scrollWidth;
            }, 100);
        }
    };

    // Handle starting drag of a timeline item
    const handleTimelineItemDragStart = (e, item) => {

        // Store the item data for the drop operation
        const dragData = {
            id: item.video_id,
            row: item.row,
            index: item.index,
            thumbnail: item.thumbnail,
            sentence: item.sentence,
            isTimelineItem: true,
            timelineItemId: item.id
        };

        e.dataTransfer.setData("text/plain", JSON.stringify(dragData));

        // Set a custom drag image if needed
        if (item.thumbnail) {
            const dragImage = new Image();
            dragImage.src = item.thumbnail;
            if (dragImage.complete) {
                e.dataTransfer.setDragImage(dragImage, 0, 0);
            }
        }

        // Add dragging class for styling
        e.currentTarget.classList.add('dragging');
    };

    const handleTimelineItemDragEnd = (e) => {
        // Remove dragging class
        e.currentTarget.classList.remove('dragging');
    };

    // Handle the drop on the main area
    const handleDrop = (e) => {
        e.preventDefault();
        setIsDragOver(false);

        try {
            const data = JSON.parse(e.dataTransfer.getData("text/plain"));
            addClipToTimeline(data);
        } catch (err) {
            console.error("Error processing dropped item:", err);
        }
    };

    // Handle drop on an insert zone (modified to handle timeline item reordering)
    const handleInsertDrop = (e, index) => {
        e.preventDefault();
        e.stopPropagation();
        setInsertIndex(-1);

        try {
            const rawData = e.dataTransfer.getData("text/plain");
            if (!rawData) {
                console.error("No data found in the drop event");
                return;
            }

            const data = JSON.parse(rawData);

            // Check if this is a timeline item being reordered
            if (data.isTimelineItem && data.timelineItemId) {
                // Find the current index of the item
                const currentIndex = timelineItems.findIndex(item => item.id === data.timelineItemId);

                if (currentIndex !== -1) {
                    // Reorder the items
                    setTimelineItems(prevItems => {
                        // Make a copy of the items
                        const updatedItems = [...prevItems];

                        // Remove the item from its current position
                        const [movedItem] = updatedItems.splice(currentIndex, 1);

                        // Adjust the target index if needed (when moving an item forward)
                        // If we're moving an item forward, the removal affects the target position
                        const adjustedIndex = currentIndex < index ? index - 1 : index;

                        // Insert at the new position
                        updatedItems.splice(adjustedIndex, 0, movedItem);

                        // Update clip labels to reflect new order
                        const finalItems = updatedItems.map((item, idx) => ({
                            ...item,
                            label: `Clip ${idx + 1}`
                        }));

                        // Update the filterStore with the new order of selected items
                        if (filterStore && currentView) {
                            // Get the video_ids in the correct order, preserving duplicates
                            const newOrderedSelections = finalItems.map(item => item.video_id).filter(id => id !== undefined);

                            // Directly set the selected sentences array in the filterStore
                            // This preserves duplicates that would be lost if we used setSelectedSentences for each item
                            if (newOrderedSelections.length > 0) {
                                filterStore.resetSelectedSentences(currentView);
                                filterStore.selectedSentences[currentView] = newOrderedSelections;
                            }
                        }

                        return finalItems;
                    });
                }
            } else {
                // Regular add from outside the timeline
                addClipToTimeline(data, index);

                // The filterStore is already updated in addClipToTimeline
            }
        } catch (err) {
            console.error("Error processing dropped item for insert:", err);
        }
    };

    const handleDragOver = (e) => {
        e.preventDefault(); // Necessary to allow drop
    };

    const handleDragEnter = (e) => {
        e.preventDefault();
        setIsDragOver(true);
    };

    const handleDragLeave = (e) => {
        e.preventDefault();

        // Only set isDragOver to false if we're truly leaving the container
        // and not just moving between child elements
        if (e.currentTarget.contains(e.relatedTarget)) {
            return;
        }

        setIsDragOver(false);
    };

    // Insert zone drag events
    const handleInsertDragEnter = (e, index) => {
        e.preventDefault();
        e.stopPropagation();
        setInsertIndex(index);
    };

    const handleInsertDragLeave = (e) => {
        e.preventDefault();
        e.stopPropagation();

        // Don't reset insertIndex if we're entering a child element
        // Check if we're moving to a child element of the same drop zone
        if (e.currentTarget.contains(e.relatedTarget)) {
            return;
        }

        setInsertIndex(-1);
    };

    // Remove a clip from the timeline
    const removeClip = (itemId) => {
        // Get the video_id before removing the item to update the filterStore
        const itemToRemove = timelineItems.find(item => item.id === itemId);

        setTimelineItems(prevItems => {
            const updatedItems = prevItems.filter(item => item.id !== itemId);

            // Update clip labels to reflect new order
            const finalItems = updatedItems.map((item, idx) => ({
                ...item,
                label: `Clip ${idx + 1}`
            }));

            // Update the filterStore with the new order of selected items
            if (filterStore && currentView) {
                // Get the video_ids in the correct order, preserving duplicates
                const newOrderedSelections = finalItems.map(item => item.video_id).filter(id => id !== undefined);

                // Directly set the selected sentences array in the filterStore
                // This preserves duplicates that would be lost if we used setSelectedSentences for each item
                filterStore.resetSelectedSentences(currentView);
                if (newOrderedSelections.length > 0) {
                    filterStore.selectedSentences[currentView] = newOrderedSelections;
                }
            }

            return finalItems;
        });
    };

    // Render insert zones between items (modified to render draggable timeline items)
    const renderInsertZones = () => {
        if (timelineItems.length === 0) return null; // No zones needed if empty

        const zones = [];

        // Add the initial insert zone (index 0)
        zones.push(
            <Box
                key="insert-zone-0"
                className={`insert-drop-zone ${insertIndex === 0 ? 'drag-over' : ''}`}
                sx={styles.insertDropZone}
                onDragEnter={(e) => handleInsertDragEnter(e, 0)}
                onDragLeave={handleInsertDragLeave}
                onDragOver={handleDragOver}
                onDrop={(e) => handleInsertDrop(e, 0)}
            >
                <ArrowDownwardIcon
                    className={`insert-indicator ${insertIndex === 0 ? 'visible' : ''}`}
                    sx={styles.insertIndicator}
                    fontSize="small"
                />
            </Box>
        );

        // Add items and subsequent insert zones
        timelineItems.forEach((item, index) => {
            // Add the item (now with drag handlers)
            zones.push(
                <Box
                    key={item.id} // Use item id for the item itself
                    sx={{
                        ...styles.timelineItem,
                        height: `calc(100% - ${styles.padding}px)`,
                        '&:hover .remove-button': {
                            display: 'flex',
                        },
                        '&.dragging': {
                            opacity: 0.6,
                            outline: `2px solid ${theme.palette.primary.main}`,
                        }
                    }}
                    draggable={true}
                    onDragStart={(e) => handleTimelineItemDragStart(e, item)}
                    onDragEnd={handleTimelineItemDragEnd}
                >
                    {/* Thumbnail image */}
                    <Box
                        component="img"
                        src={item.thumbnail}
                        alt={item.label} // Keep alt text even if label isn't displayed
                        sx={styles.thumbnail}
                    />
                    {/* Optional: Short text preview - make it smaller */}
                    <Typography
                        variant="caption"
                        noWrap
                        sx={{
                            fontSize: styles.fontSize.small,
                            color: theme.palette.text.secondary,
                            maxWidth: styles.textPreviewMaxWidth,
                            textAlign: 'center',
                            lineHeight: 1,
                            mt: styles.smallPadding
                        }}
                    >
                        {item.sentence?.substring(0, 15)}...
                    </Typography>
                    {/* Remove button (shows on hover) */}
                    <IconButton
                        className="remove-button"
                        onClick={() => removeClip(item.id)}
                        sx={styles.removeButton}
                    >
                        <CloseIcon sx={{ color: 'white', backgroundColor: 'red', borderRadius: '50%' }} />
                    </IconButton>
                </Box>
            );

            // Add the insert zone after this item (index + 1)
            const nextIndex = index + 1;
            zones.push(
                <Box
                    key={`insert-zone-${nextIndex}`}
                    className={`insert-drop-zone ${insertIndex === nextIndex ? 'drag-over' : ''} ${nextIndex === timelineItems.length ? 'end-zone' : ''}`} // Add end-zone class if it's the last one
                    sx={nextIndex === timelineItems.length ? styles.endDropZone : styles.insertDropZone} // Use endDropZone style if last
                    onDragEnter={(e) => handleInsertDragEnter(e, nextIndex)}
                    onDragLeave={handleInsertDragLeave}
                    onDragOver={handleDragOver}
                    onDrop={(e) => handleInsertDrop(e, nextIndex)}
                >
                    {nextIndex === timelineItems.length ? (
                        <Typography variant="caption" sx={{ color: theme.palette.text.secondary }}>
                            Drop to add
                        </Typography>
                    ) : (
                        <ArrowDownwardIcon
                            className={`insert-indicator ${insertIndex === nextIndex ? 'visible' : ''}`}
                            sx={styles.insertIndicator}
                            fontSize="small"
                        />
                    )}
                </Box>
            );
        });

        return zones;
    };

    let contentStyle = {}
    if (toolbarPosition !== 'top' && toolbarPosition !== 'bottom') {
        contentStyle = getContentStyle(toolbarPosition)
    }

    return (
        <Box
            ref={containerRef}
            sx={{
                ...styles.timelineContainer,
                bottom: `${bottomOffset}px`,
                height: `${height}px`,
                ...(isDragOver && insertIndex === -1 ? styles.dropTarget : {}),
                ...contentStyle
            }}
            onDrop={handleDrop}
            onDragOver={handleDragOver}
            onDragEnter={handleDragEnter}
            onDragLeave={handleDragLeave}
        >
            {/* Inner container for the scrollable content */}
            <Box sx={styles.timelineContent}>
                {timelineItems.length > 0 ? (
                    /* Render timeline items with insert zones between them */
                    renderInsertZones()
                ) : (
                    <Box sx={styles.emptyStateMessage}>
                        <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
                            {isDragOver ? 'Drop to add to timeline' : 'Drag videos here to create a timeline'}
                        </Typography>
                    </Box>
                )}
            </Box>
        </Box>
    );
});

export default Timeline; 